import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import RoomCard from './RoomCard';
import { MRIMachinePopulated, NO_LOCATION } from './types';
import { LocationSection } from './roomOverviewUtils';

interface MachineGridProps {
    sections: LocationSection[];
    hasLocations: boolean;
    collapsedSections: Set<string>;
    onToggleSection: (key: string) => void;
    favoritedMachineIds: Set<string>;
    onFavoriteChange: (machineId: string, isFavorited: boolean) => void;
    onClick: (machineId: string) => void;
}

export default function MachineGrid({
    sections,
    hasLocations,
    collapsedSections,
    onToggleSection,
    favoritedMachineIds,
    onFavoriteChange,
    onClick,
}: MachineGridProps) {
    const { t } = useTranslation();

    const renderCards = (items: MRIMachinePopulated[]) => (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((machine) => (
                <RoomCard
                    key={machine._id}
                    machine={machine}
                    isFavorited={favoritedMachineIds.has(machine._id)}
                    onFavoriteChange={onFavoriteChange}
                    onClick={onClick}
                />
            ))}
        </div>
    );

    if (!hasLocations) {
        return renderCards(sections.flatMap((s) => s.machines));
    }

    return (
        <div className="space-y-8">
            {sections.map((section) => {
                const collapsed = collapsedSections.has(section.key);
                return (
                    <section key={section.key} className="space-y-4">
                        <button
                            type="button"
                            onClick={() => onToggleSection(section.key)}
                            aria-expanded={!collapsed}
                            className="flex w-full items-center gap-2 border-b border-subtle pb-2 text-left"
                        >
                            <ChevronDown
                                className={cn(
                                    'h-4 w-4 shrink-0 text-subtle-foreground transition-transform',
                                    collapsed && '-rotate-90'
                                )}
                                aria-hidden="true"
                            />
                            <h2 className="text-base font-medium">
                                {section.key === NO_LOCATION
                                    ? t(
                                          'roomOverview.roomOverview.sections.other'
                                      )
                                    : section.key}
                            </h2>
                            <span className="text-xs font-medium text-subtle-foreground">
                                {t(
                                    'roomOverview.roomOverview.sections.roomCount',
                                    {
                                        count: section.machines.length,
                                    }
                                )}
                            </span>
                        </button>
                        {!collapsed && renderCards(section.machines)}
                    </section>
                );
            })}
        </div>
    );
}
